/**
 * LeadCard — one row in the מתעניינים list (Leads v2, 2026 row anatomy).
 * Pure presentational; every action is a callback. Icon-only controls get a
 * real HoverTip (native `title` tooltips are too slow / absent on touch).
 */
import {
  Phone,
  Envelope,
  WhatsappLogo,
  ChatText,
  UserSwitch,
  Pencil,
  Trash,
  Plugs,
  PencilSimple,
  CalendarCheck,
  UploadSimple,
  Repeat,
  ArrowCounterClockwise,
  Clock,
} from "@phosphor-icons/react";
import HoverTip from "../ui/HoverTip";
import LeadStatusSelect from "./LeadStatusSelect";
import { getLeadSourceChannel } from "../../utils/leadContract";
import {
  statusStyleVars,
  displayPhone,
  whatsappHref,
  telHref,
  getFollowUpState,
  FOLLOW_UP_LABELS,
  formatFollowUp,
  sourceChannelLabel,
} from "./leadsUtils";

const SOURCE_ICONS = {
  manual: PencilSimple,
  webhook: Plugs,
  booking: CalendarCheck,
  import: UploadSimple,
};

/** Icon button / link with a visible tooltip. */
function IconAction({ tip, href, external, className = "", onClick, disabled, children }) {
  const cls = `ld-icon-btn ${className}`.trim();
  const inner = href ? (
    <a
      className={cls}
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      aria-label={tip}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </a>
  ) : (
    <button type="button" className={cls} onClick={onClick} disabled={disabled} aria-label={tip}>
      {children}
    </button>
  );
  return <HoverTip text={tip}>{inner}</HoverTip>;
}

function LeadCard({
  lead,
  status,
  statuses,
  isDuplicate,
  isDeletedView = false,
  isAdmin = false,
  onOpen,
  onStatusChange,
  onConvert,
  onEdit,
  onDelete,
  onRestore,
  onHardDelete,
  busy = false,
}) {
  const followState = getFollowUpState(lead);
  const channel = getLeadSourceChannel(lead);
  const SourceIcon = SOURCE_ICONS[channel] || PencilSimple;
  const wa = whatsappHref(lead.phone);
  const tel = telHref(lead.phone);
  const isArchived = lead.status === "converted" || lead.status === "lost" || !!lead.convertedToPatientId;
  const sourceTitle =
    channel === "webhook"
      ? `נקלט אוטומטית${lead.source?.keyLabel ? ` · ${lead.source.keyLabel}` : ""}`
      : sourceChannelLabel(lead);

  const handleKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen?.(lead);
    }
  };

  return (
    <div
      className={`ld-card${isArchived ? " ld-card-archived" : ""}${isDeletedView ? " ld-card-deleted" : ""}${followState === "overdue" ? " ld-card-overdue" : ""}`}
      style={statusStyleVars(status)}
    >
      <span className="ld-dot" aria-hidden="true" />

      <div className="ld-card-main" role="button" tabIndex={0} onClick={() => onOpen?.(lead)} onKeyDown={handleKey}>
        <div className="ld-card-title-row">
          <h3 className="ld-card-name">{lead.name}</h3>
          {isDuplicate && (
            <HoverTip text="קיים מתעניין נוסף עם אותו טלפון/אימייל">
              <span className="ld-chip ld-chip-dup" tabIndex={0}>
                <Repeat size={12} weight="bold" />
                מתעניין חוזר
              </span>
            </HoverTip>
          )}
          {followState && (
            <HoverTip text={`מועד המעקב הבא: ${formatFollowUp(lead)}`}>
              <span className={`ld-chip ld-chip-follow ld-follow-${followState}`} tabIndex={0}>
                <Clock size={12} weight="bold" />
                {followState === "upcoming" ? formatFollowUp(lead) : FOLLOW_UP_LABELS[followState]}
              </span>
            </HoverTip>
          )}
          {lead.developmentLogs?.length > 0 && (
            <HoverTip text="תיעודי התפתחות">
              <span className="ld-chip ld-chip-neutral" tabIndex={0}>
                <ChatText size={12} weight="fill" />
                {lead.developmentLogs.length}
              </span>
            </HoverTip>
          )}
        </div>

        <div className="ld-card-meta">
          <HoverTip text={sourceTitle}>
            <span className="ld-meta-item ld-meta-source" tabIndex={0}>
              <SourceIcon size={13} weight="regular" />
              <span>{channel === "webhook" && lead.source?.keyLabel ? lead.source.keyLabel : lead.referralSource || sourceChannelLabel(lead)}</span>
            </span>
          </HoverTip>
          {lead.phone && (
            <span className="ld-meta-item ld-meta-phone">
              <Phone size={13} weight="regular" />
              <span dir="ltr">{displayPhone(lead.phone)}</span>
            </span>
          )}
          {lead.email && (
            <span className="ld-meta-item ld-meta-email">
              <Envelope size={13} weight="regular" />
              <span dir="ltr">{lead.email}</span>
            </span>
          )}
          {lead.agreedSessionCost ? <span className="ld-meta-item">₪{lead.agreedSessionCost}</span> : null}
        </div>
      </div>

      {/* Contact shortcuts — pure links, no server */}
      {(wa || tel) && !isDeletedView && (
        <div className="ld-card-contact">
          {tel && (
            <IconAction tip="התקשר/י" href={tel}>
              <Phone size={16} weight="regular" />
            </IconAction>
          )}
          {wa && (
            <IconAction tip="שלח/י WhatsApp" href={wa} external className="ld-icon-wa">
              <WhatsappLogo size={16} weight="regular" />
            </IconAction>
          )}
        </div>
      )}

      {/* Status dropdown (deliberate action, status-tinted) */}
      {!isDeletedView && (
        <LeadStatusSelect
          value={lead.status}
          status={status}
          statuses={statuses}
          excludeValues={["converted"]}
          disabled={isArchived}
          busy={busy}
          onChange={(v) => onStatusChange?.(lead, v)}
        />
      )}

      <div className="ld-card-actions">
        {isDeletedView ? (
          isAdmin && (
            <>
              <IconAction tip="שחזר/י מתעניין" onClick={() => onRestore?.(lead)} disabled={busy}>
                <ArrowCounterClockwise size={17} weight="regular" />
              </IconAction>
              <IconAction tip="מחק/י לצמיתות" className="ld-icon-danger" onClick={() => onHardDelete?.(lead)} disabled={busy}>
                <Trash size={17} weight="regular" />
              </IconAction>
            </>
          )
        ) : (
          <>
            {!isArchived && (
              <IconAction tip="הפוך/י למטופל" className="ld-icon-convert" onClick={() => onConvert?.(lead)} disabled={busy}>
                <UserSwitch size={17} weight="regular" />
              </IconAction>
            )}
            <IconAction tip="ערוך/י" onClick={() => onEdit?.(lead)} disabled={busy}>
              <Pencil size={17} weight="regular" />
            </IconAction>
            <IconAction tip="מחק/י (ניתן לשחזר)" className="ld-icon-danger" onClick={() => onDelete?.(lead)} disabled={busy}>
              <Trash size={17} weight="regular" />
            </IconAction>
          </>
        )}
      </div>
    </div>
  );
}

export default LeadCard;
